import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@app/components/ui/button";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/macro";
import { CheckCircle2, ArrowRight, Database, Loader2, X } from "lucide-react";
import { getSpecialityUi, type SpecialityDescriptor } from "@app/utils/speciality";

interface UserConnectionItem {
  id: string;
  tenant_id: string;
  url: string;
  has_credentials: boolean;
}

interface OnboardingWizardProps {
  userConnections: UserConnectionItem[];
  specialite: string | null;
  supportedSpecialites: SpecialityDescriptor[];
  onSpecialiteChosen: (id: string) => Promise<void>;
  onDismiss: () => void;
}

const STEPS = ["Bienvenue", "Spécialité", "Source de données"];

export default function OnboardingWizard({
  userConnections,
  specialite,
  supportedSpecialites,
  onSpecialiteChosen,
  onDismiss,
}: OnboardingWizardProps) {
  const navigate = useNavigate();
  const { t } = useLingui();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const hasConnection = userConnections.length > 0;

  const handleSelectSpecialite = async (id: string) => {
    setSaving(true);
    await onSpecialiteChosen(id);
    setSaving(false);
    setStep(2);
  };

  const handleFinish = () => {
    onDismiss();
    navigate("/dashboard");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <span className="font-bold text-lg text-slate-900">MAGE-X</span>
            <span className="text-slate-300">—</span>
            <span className="text-sm text-slate-500">
              <Trans>Configuration initiale</Trans>
            </span>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            title="Passer pour l'instant"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step progress */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold transition-all ${
                  i < step
                    ? "bg-primary text-white"
                    : i === step
                      ? "bg-primary/10 text-primary border border-primary"
                      : "bg-slate-100 text-slate-400"
                }`}
              >
                {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className={`text-xs hidden sm:inline ${
                  i === step ? "text-slate-700 font-medium" : "text-slate-400"
                }`}
              >
                {label}
              </span>
              {i < STEPS.length - 1 && (
                <div className={`h-px w-6 mx-1 ${i < step ? "bg-primary" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 py-6 min-h-[280px]">
          {/* Step 0 — Bienvenue */}
          {step === 0 && (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  <Trans>Bienvenue sur MAGE-X</Trans> 👋
                </h2>
                <p className="text-slate-600 mt-2 leading-relaxed">
                  <Trans>
                    MAGE-X extrait automatiquement des données structurées depuis vos dossiers patients
                    et les agrège dans un tableau de bord clinique.
                  </Trans>
                </p>
              </div>
              <ul className="space-y-2">
                {[
                  { icon: "🦴", text: "Pipeline d'extraction IA adapté à votre spécialité" },
                  { icon: "📊", text: "Tableau de bord avec scores cliniques validés" },
                  { icon: "🔗", text: "Connexion à votre logiciel métier" },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3 text-sm text-slate-700">
                    <span className="text-base shrink-0">{item.icon}</span>
                    {item.text}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400">
                <Trans>Cette configuration ne prend que 2 minutes.</Trans>
              </p>
            </div>
          )}

          {/* Step 1 — Spécialité */}
          {step === 1 && (
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  <Trans>Quelle est votre spécialité ?</Trans>
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  <Trans>
                    Cela configure automatiquement le pipeline d'extraction, l'ontologie et le modèle IA.
                  </Trans>
                </p>
              </div>
              <div className="space-y-3">
                {supportedSpecialites.map((sp) => {
                  const ui = getSpecialityUi(sp.id, t, sp);
                  const isSelected = specialite === sp.id;
                  return (
                    <button
                      key={sp.id}
                      onClick={() => !isSelected && handleSelectSpecialite(sp.id)}
                      disabled={saving}
                      className={`w-full text-left rounded-xl border p-4 transition-all ${
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-slate-200 hover:border-primary/50 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{ui.emoji}</span>
                          <div>
                            <p className="font-semibold text-slate-900">{ui.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{ui.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ui.features.map((f) => (
                                <span
                                  key={f}
                                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                                >
                                  <CheckCircle2 className="h-2.5 w-2.5" />
                                  {f}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0 mt-1" />
                        ) : isSelected ? (
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        ) : null}
                      </div>
                    </button>
                  );
                })}

                {/* Placeholder */}
                <div className="w-full rounded-xl border border-dashed border-slate-200 p-4 opacity-50">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">➕</span>
                    <div>
                      <p className="font-medium text-slate-500 text-sm">
                        <Trans>Autres spécialités — bientôt disponibles</Trans>
                      </p>
                      <p className="text-xs text-slate-400">
                        <Trans>Orthopédie générale, médecine générale…</Trans>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Source de données */}
          {step === 2 && (
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  <Trans>Source de données</Trans>
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  <Trans>
                    Connectez votre logiciel métier pour synchroniser automatiquement vos patients.
                  </Trans>
                </p>
              </div>
              {hasConnection ? (
                <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">
                      <Trans>Source de données connectée</Trans>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {userConnections[0].url}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Database className="h-5 w-5 text-slate-400" />
                    <p className="text-sm font-medium">
                      <Trans>Aucune source connectée</Trans>
                    </p>
                  </div>
                  <p className="text-xs text-slate-500">
                    <Trans>
                      Vous pouvez configurer une connexion (logiciel métier, import de fichiers…)
                      depuis la page Configuration à tout moment.
                    </Trans>
                  </p>
                </div>
              )}
              <p className="text-xs text-slate-400">
                <Trans>
                  Vous pouvez aussi importer des fichiers PDF ou CSV manuellement depuis l'onglet « Fichiers importés ».
                </Trans>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 pb-6 pt-2">
          <button
            onClick={onDismiss}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            <Trans>Passer pour l'instant</Trans>
          </button>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep((s) => s - 1)}
                disabled={saving}
              >
                <Trans>Retour</Trans>
              </Button>
            )}

            {step === 0 && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90"
                onClick={() => setStep(1)}
              >
                <Trans>Commencer</Trans>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}

            {step === 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep(2)}
                disabled={saving}
              >
                <Trans>Passer cette étape</Trans>
              </Button>
            )}

            {step === 2 && (
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90"
                onClick={handleFinish}
              >
                <Trans>Accéder au tableau de bord</Trans>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
